import '@static/Typescripts/Global/Window';

// axios is imported as an ESM module here, not used as a global. Static's
// @types/global.d.ts declares `axios` as a global for legacy OfficeWeb-style
// consumers that load axios.min.js via a CDN <script> — PowerphoneWeb instead
// bundles it through Vite, so the local import shadows that declaration.
import axios from 'axios';
import type { AxiosError } from 'axios';
import { initLanguageOverlay } from '../Assets/languageOverlay';
import { initHeaderMenu } from '../Assets/headerMenu';

axios.defaults.headers.common['Content-Type'] = 'application/json';
// Attach Authorization only when a token actually exists. Interpolating a null getItem would
// send the literal header `Bearer null` on EVERY request — the API reads that as a malformed
// credential, and the 401 it answers with trips the interceptor's /authorize redirect below,
// bouncing anonymous visitors off the page.
const token = sessionStorage.getItem('token');
if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}
axios.defaults.timeout = 5000;

axios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {

        const status = error.response?.status;

        if (status === 401 || status === 403) {
            localStorage.setItem("redirect", window.location.href);
            window.location.href =
                `${window.webApiUrl}/authorize?redirect=${encodeURIComponent(window.location.host)}`;
            return Promise.reject(error);
        }

        let message: string;

        if (error.code === "ECONNABORTED") {
            message = "Request timed out. Please try again later.";
        } else if (!error.response) {
            message = "Unable to connect to the server. Please check your network connection.";
        } else {
            const backendMsg = (error.response.data as { message?: string } | null | undefined)?.message;
            switch (status) {
                case 400:
                    message = backendMsg ?? "Bad request. Please verify your parameters.";
                    break;
                case 404:
                    message = backendMsg ?? "Resource not found.";
                    break;
                case 409:
                    message = backendMsg ?? "Conflict detected: the resource already exists.";
                    break;
                case 500:
                    message = backendMsg ?? "Internal server error. Please try again later.";
                    break;
                default:
                    message = backendMsg ?? `Server error (${status}). Please try again later.`;
            }
        }

        window.toast(message);
        return Promise.reject(error);

    }
);


// App-entry marker — the Android app opens the site in a WebView and tags it with
// ?from=android. Persist that value in localStorage once, so EVERY later page — even ones
// navigated to WITHOUT the query string — can tell it's inside the app and drop the site
// chrome (header, footer, bottom CTA), matching the server-side embedded mode.
// The server already drops the chrome when the query string IS present (it skips rendering
// those partials); this client path covers the pages where it isn't, by adding
// body.embedded to hide the rendered chrome (see _Layout.cshtml.scss). localStorage is
// synchronous, so this runs (and hides) as early as the deferred module executes.
const params = new URLSearchParams(window.location.search);

const fromParam = params.get('from');
if (fromParam) {
    localStorage.setItem('from', fromParam);
}
if (localStorage.getItem('from') === 'android') {
    document.body.classList.add('embedded');
}

// App-passed email — the Android app appends ?email=<user> so the site knows who's
// signed in. Persist it (globally, like `from`) so any later page can prefill it — e.g.
// the feedback form's email field (Feedback.cshtml.ts).
const emailParam = params.get('email');
if (emailParam) {
    localStorage.setItem('email', emailParam);
}


// Header primary menu — the inline bar's dropdowns (md+) and the drawer that
// replaces the bar below md. Wired here (layout level) since the header is chrome; a
// no-op in embedded mode, where the header isn't rendered at all.
initHeaderMenu();

// Language & region popup — the header + footer globe buttons ([data-language-trigger])
// open a full-screen overlay whose body is fetched from /language on first open. Wired
// here (layout level) since the triggers and the overlay shell are both chrome.
initLanguageOverlay();


// Header headroom — once the header has scrolled out of view, scrolling UP
// reveals it immediately (pinned to the top) instead of forcing the user all
// the way back to the page top. Scrolling DOWN hides it again; within the
// header's own height of the top it returns to its natural in-flow position.
// The .pinned / .unpinned transforms live in Partials/Header.cshtml.scss; here
// we only detect scroll direction (rAF-throttled, passive listener).
const header = document.querySelector<HTMLElement>('header.main');
if (header) {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateHeader = () => {
        const currentScrollY = window.scrollY;

        if (currentScrollY <= header.offsetHeight) {
            // Near the top — the header is (partly) in view; let it sit in flow.
            header.classList.remove('pinned', 'unpinned');
        } else if (currentScrollY > lastScrollY) {
            // Scrolling down, header already out of view — keep it hidden.
            header.classList.add('unpinned');
            header.classList.remove('pinned');
        } else if (currentScrollY < lastScrollY) {
            // Scrolling up — reveal it right away.
            header.classList.add('pinned');
            header.classList.remove('unpinned');
        }

        lastScrollY = currentScrollY;
        ticking = false;
    };

    window.addEventListener('scroll', () => {
        if (!ticking) {
            ticking = true;
            window.requestAnimationFrame(updateHeader);
        }
    }, { passive: true });
}


// Layout portal — one always-present container (#layout-portal) holding at most a
// single transient dropdown (its firstElementChild). Queried ONCE here, not per
// event; the dropdown is dismissed on an outside click, on resize, and on scroll.
const portal = document.querySelector<HTMLElement>('#layout-portal');
const clearPortal = (): void => portal?.firstElementChild?.remove();

// Outside-click dismiss — the portal's single child names its own trigger via data-id, so a
// click anywhere outside that trigger's subtree closes it. This is a deferred module script,
// so the DOM is already parsed when it runs (same as the header blocks above) — no
// DOMContentLoaded wrapper needed.
document.addEventListener('click', (e: MouseEvent) => {
    const portalChild = portal?.firstElementChild as HTMLElement | null;
    const id = portalChild?.dataset.id;
    if (!portalChild || !id) {
        return;
    }
    const target = e.target as Element | null;
    if (target && !target.closest(`[data-id="${id}"]`)) {
        portalChild.remove();
    }
});

// Dismiss the open portal dropdown on resize / scroll. Passive scroll + the cached portal
// node — no per-event DOM query, no scroll-blocking.
window.addEventListener('resize', clearPortal);
window.addEventListener('scroll', clearPortal, { passive: true });

// Top page loader — hide it once the page (images included) has finished loading.
window.addEventListener('load', () => {
    const loader = document.querySelector<HTMLElement>('#layout-loader');
    if (loader) loader.style.display = 'none';
});